"use client";

import { HeaderClickItem } from "@faire/retailer-visitor-shared/components/Header/HeaderClickItem";
import { NotificationBellWithStreaks } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/__internal__/NotificationBellWithStreaks";
import { useHasHighPriorityNotification } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/__internal__/NotificationDropdown/hooks/useHasHighPriorityNotification";
import { useHasUnreadNotification } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/__internal__/NotificationDropdown/hooks/useHasUnreadNotification";
import { useShouldPerformHighPriorityAction } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/__internal__/NotificationDropdown/hooks/useShouldPerformHighPriorityAction";
import { LazyNotificationDropdown } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/__internal__/NotificationDropdown/index";
import { ANIMATION_TIME } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/consts";
import { useIsOnNotificationsPage } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useIsOnNotificationsPage";
import { useNotificationPreview } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useNotificationPreview";
import { useSetNotificationState } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useSetNotificationState";
import { IconWithMaybeDot } from "@faire/retailer-visitor-shared/components/Header/icons/IconWithMaybeDot";
import {
  HeaderDropdown,
  HeaderIconContainer,
} from "@faire/retailer-visitor-shared/components/Header/styled";
import { useHeaderPriorityStore } from "@faire/retailer-visitor-shared/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { useIsHeaderDropdownVisible } from "@faire/retailer-visitor-shared/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useDelayedCallback } from "@faire/web--source/ui/hooks/useDelayedCallback";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { useViewportWidth } from "@faire/web--source/ui/hooks/useViewportWidth";
import { useInvalidateQuery as useInvalidateGetNotificationQuery } from "@faire/web-api--source/endpoints/www/api/retailer/notifications/get-hooks";
import { trackRetailerNotificationsNotificationBellClick } from "@faire/web-api--source/events/retailerNotifications/click/notificationBell";
import notificationsPage from "@faire/web-api--source/routes/www/user/notifications";
import isUndefined from "lodash/isUndefined";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { MAX_HEADER_WIDTH } from "../consts";

export const HeaderNotificationsDropdown = () => {
  const history = useHistory();
  const { isMobile } = useViewport();
  const isSSR = useIsSSR();
  const { retailer, retailerToken } = useRetailer();
  const hasHighPriorityNotification = useHasHighPriorityNotification();
  const { hasUnreadNotification } = useHasUnreadNotification();
  const isOnNotificationsPage = useIsOnNotificationsPage();
  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const [shouldShowNotificationDot, setShouldShowNotificationDot] = useState(
    hasUnreadNotification
  );
  const notificationPreview = useNotificationPreview();
  const { setNudgeHasBeenDismissed } = useSetNotificationState(
    notificationPreview?.token ?? ""
  );
  const invalidateGetNotificationQuery = useInvalidateGetNotificationQuery();
  const [{ isNotificationPreviewVisible }] = useHeaderPriorityStore([
    "isNotificationPreviewVisible",
  ]);
  const shouldPerformHighPriorityAction = useShouldPerformHighPriorityAction();

  const { setHeaderDropdownVisibility } = useIsHeaderDropdownVisible();

  const viewportWidth = useViewportWidth((width) => width);

  // place the dropdown at the right edge of the header
  const rightPosition =
    viewportWidth <= MAX_HEADER_WIDTH
      ? 0
      : (viewportWidth - MAX_HEADER_WIDTH) / 2;

  useEffect(() => {
    setShouldShowNotificationDot(hasUnreadNotification);
  }, [hasUnreadNotification]);

  useEffect(() => {
    if (isOnNotificationsPage) {
      setShouldShowNotificationDot(false);
    }
  }, [isOnNotificationsPage]);

  const delayedShowDropdown = useDelayedCallback(() => {
    setShouldShowDropdown(true);
  });

  const toggleDropdownHandler = useCallback(
    (shouldShowDropdown: boolean, isOutsideClick: boolean) => {
      if (hasUnreadNotification && !shouldShowDropdown && !isOutsideClick) {
        // Invalidate query if there was an unread notification and the dropdown was closed
        invalidateGetNotificationQuery();
      }

      if (hasUnreadNotification && shouldShowDropdown) {
        setShouldShowNotificationDot(false);
      }

      if (
        isNotificationPreviewVisible &&
        shouldShowDropdown &&
        !isOutsideClick
      ) {
        delayedShowDropdown(ANIMATION_TIME);
      } else {
        setShouldShowDropdown(shouldShowDropdown);
      }

      setHeaderDropdownVisibility("NOTIFICATIONS", shouldShowDropdown);
    },
    [
      delayedShowDropdown,
      hasUnreadNotification,
      invalidateGetNotificationQuery,
      isNotificationPreviewVisible,
      setHeaderDropdownVisibility,
    ]
  );

  const gotoNotificationPage = debounceIfPossible(() => {
    history.push(notificationsPage());
  });

  const handleClick = () => {
    if (isMobile && !isOnNotificationsPage) {
      gotoNotificationPage();
    }

    trackRetailerNotificationsNotificationBellClick(retailerToken ?? "");
    if (
      hasHighPriorityNotification &&
      notificationPreview &&
      isUndefined(notificationPreview?.nudge_dismissed_at)
    ) {
      setNudgeHasBeenDismissed();
    }
  };

  if (retailer === undefined) {
    return null;
  }

  const shouldRenderDropdown = !isMobile;

  const shouldRenderNotificationDot =
    !isSSR &&
    ((hasUnreadNotification &&
      shouldShowNotificationDot &&
      !isNotificationPreviewVisible &&
      !hasHighPriorityNotification) ||
      shouldPerformHighPriorityAction);

  const notificationDropdown = !shouldRenderDropdown ? null : (
    <HeaderDropdown
      $right={`${rightPosition}px`}
      $width="392px"
      $maxHeight="calc(100vh - 110px)"
    >
      <Suspense fallback={null}>
        <LazyNotificationDropdown
          hideDropdown={() => setShouldShowDropdown(false)}
        />
      </Suspense>
    </HeaderDropdown>
  );

  return (
    <>
      <Spacer width={12} />
      <HeaderClickItem
        clickChild={notificationDropdown}
        dataTestId="notificationDropdown"
        toggleDropdown={toggleDropdownHandler}
        shouldShowDropdown={shouldShowDropdown}
        shouldDisableClick={!shouldRenderDropdown}
        onClick={handleClick}
      >
        <HeaderIconContainer>
          <IconWithMaybeDot
            icon={
              <NotificationBellWithStreaks
                shouldAnimate={shouldPerformHighPriorityAction}
                delayAnimationTime={
                  shouldPerformHighPriorityAction ? 1250 : 2000
                }
              />
            }
            hasHighPriority={hasHighPriorityNotification}
            shouldAnimateDot={shouldRenderNotificationDot}
            dataTestId="unread-notification-dot"
            shouldShowDot={shouldRenderNotificationDot}
            isActive={shouldShowDropdown && !isMobile}
            delayAnimationTime={shouldPerformHighPriorityAction ? 0 : 750}
          />
        </HeaderIconContainer>
      </HeaderClickItem>
      <Spacer width={{ mobileAndAbove: 0, desktopAndAbove: "2x" }} />
    </>
  );
};
